import "./theme.css";
import { aeraContent } from "./content";
import { chapterState } from "./film/chapterState";
import { mountChapterPlayer } from "./film/createChapterPlayer";
import { aeraWorldManifest } from "./film/worldManifest";

const itinerary = aeraContent.itinerary.map((item) => `
  <article class="ritual">
    <time>${item.time}</time>
    <div><h3>${item.title}</h3><p>${item.description}</p></div>
  </article>`).join("");

const packages = aeraContent.packages.map((item, index) => `
  <article class="package${index === 1 ? " package--signal" : ""}">
    <span class="package__number">0${index + 1}</span>
    <div>
      <h3>${item.name}</h3>
      <p class="package__meta">${item.duration} · ${item.occupancy}</p>
      <ul>${item.inclusions.map((value) => `<li>${value}</li>`).join("")}</ul>
    </div>
    <div class="package__foot">
      <span>${item.price}</span>
      <button type="button" data-enquire="${item.id}">Enquire ↗</button>
    </div>
  </article>`).join("");

const chapterCopy = aeraWorldManifest.sections.map((section, index) => `
  <article class="chapter-copy chapter-copy--${index % 2 ? "right" : "left"}" data-chapter-copy="${index}">
    <div class="chapter-copy__line"></div>
    <p>${section.eyebrow}</p>
    <h2>${section.title}</h2>
    <span>${section.body}</span>
  </article>`).join("");

document.querySelector<HTMLDivElement>("#app")!.innerHTML = `
  <header class="nav">
    <a class="mark" href="#top" aria-label="AERA home">AERA</a>
    <div class="nav__location" aria-live="polite">
      <span id="chapter-number">01</span><i></i><strong id="chapter-name">The clearing</strong>
    </div>
    <a class="nav__menu" href="#stays">Stay</a>
  </header>

  <main id="top">
    <section id="journey" class="cinema" aria-label="AERA property film">
      <div class="cinema-stage">
        <div id="chapter-player" class="chapter-player"></div>
        <div class="cinema-stage__grade"></div>
        <div class="cinema-stage__grain"></div>
        <div class="cinema-stage__opening">
          <p>${aeraContent.brand.descriptor}</p>
          <h1>AERA</h1>
          <span>Scroll to begin</span>
        </div>
        <div class="chapter-copy-list">${chapterCopy}</div>
        <aside class="field-index" aria-hidden="true">
          <span>56° 48′ N</span>
          <div><i></i></div>
          <span id="field-progress">00</span>
        </aside>
        <button class="film-control" type="button" aria-label="Pause active film">
          <span class="film-control__icon">Ⅱ</span><span class="film-control__label">Pause film</span>
        </button>
        <p class="cinema-stage__credit">A living property portrait · Beyond Static Studio</p>
      </div>
      <div class="chapter-triggers" aria-hidden="true">
        ${aeraWorldManifest.sections.map(() => "<div></div>").join("")}
      </div>
    </section>

    <section class="manifesto">
      <p class="eyebrow">The premise</p>
      <h2>Rest is not an activity.<br />It is the absence of demand.</h2>
      <p class="manifesto__body">AERA was drawn around the things we chose not to add. No television. No itinerary competing for your attention. Just architecture, weather and enough space for your thoughts to change pace.</p>
    </section>

    <section class="details" aria-label="The spaces">
      <header><p class="eyebrow">Four rooms. Nothing extra.</p><h2>Built to hold<br />the quiet.</h2></header>
      <div class="details__grid">
        ${aeraContent.hotspots.map((item, index) => `
          <article><span>0${index + 1}</span><h3>${item.label}</h3><p>${item.detail}</p></article>`).join("")}
      </div>
    </section>

    <section class="rituals">
      <header><p class="eyebrow">One unhurried day</p><h2>Follow the light,<br />not the clock.</h2></header>
      <div class="rituals__list">${itinerary}</div>
    </section>

    <section class="quote">
      <blockquote>“${aeraContent.testimonial.quote}”</blockquote>
      <cite>${aeraContent.testimonial.attribution}</cite>
    </section>

    <section id="stays" class="stays">
      <header><p class="eyebrow">Choose your distance</p><h2>Stay long enough<br />to arrive.</h2></header>
      <div class="packages">${packages}</div>
    </section>

    <section class="finale">
      <p>${aeraContent.brand.descriptor}</p>
      <h2>The road ends.<br />You begin again.</h2>
      <button type="button" data-enquire>Check availability <span>↗</span></button>
    </section>
  </main>

  <footer>
    <a class="mark" href="#top">AERA</a>
    <p>${aeraContent.disclosure}</p>
    <a href="${aeraContent.studioUrl}">A living website by Beyond Static Studio ↗</a>
  </footer>

  <dialog id="enquiry">
    <form method="dialog">
      <button class="dialog__close" value="close" aria-label="Close enquiry">×</button>
      <p class="eyebrow">AERA is a portfolio experience</p>
      <h2>Imagine your retreat<br />told like this.</h2>
      <p>This fictional stay demonstrates what Beyond Static Studio can create for a real retreat, cabin or boutique hospitality brand.</p>
      <a href="${aeraContent.studioUrl}/#contact">Build my retreat website ↗</a>
    </form>
  </dialog>`;

const dialog = document.querySelector<HTMLDialogElement>("#enquiry")!;
document.querySelectorAll<HTMLButtonElement>("[data-enquire]").forEach((button) => {
  button.addEventListener("click", () => dialog.showModal());
});

const cinema = document.querySelector<HTMLElement>(".cinema")!;
const stage = document.querySelector<HTMLElement>(".cinema-stage")!;
const copies = [...document.querySelectorAll<HTMLElement>("[data-chapter-copy]")];
const chapterNumber = document.querySelector<HTMLElement>("#chapter-number")!;
const chapterName = document.querySelector<HTMLElement>("#chapter-name")!;
const fieldProgress = document.querySelector<HTMLElement>("#field-progress")!;
const filmControl = document.querySelector<HTMLButtonElement>(".film-control")!;
const motionQuery = matchMedia("(prefers-reduced-motion: reduce)");
const player = mountChapterPlayer(document.querySelector<HTMLElement>("#chapter-player")!, aeraWorldManifest, {
  viewportWidth: innerWidth,
  reducedMotion: motionQuery.matches
});

let activeIndex = -1;
let frame = 0;
let running = true;

const render = () => {
  frame = 0;
  if (!running) return;
  const rect = cinema.getBoundingClientRect();
  const progress = Math.max(0, Math.min(1, -rect.top / Math.max(1, rect.height - innerHeight)));
  const state = chapterState(progress, aeraWorldManifest.sections.length);

  if (state.index !== activeIndex) {
    activeIndex = state.index;
    player.activate(activeIndex);
    const section = aeraWorldManifest.sections[activeIndex];
    chapterNumber.textContent = String(activeIndex + 1).padStart(2, "0");
    chapterName.textContent = section.eyebrow.replace(/^\d+\s*·\s*/, "");
    copies.forEach((copy, index) => copy.classList.toggle("is-active", index === activeIndex));
    stage.dataset.chapter = String(activeIndex);
  }

  stage.style.setProperty("--chapter-local", state.localProgress.toFixed(4));
  stage.style.setProperty("--journey-progress", progress.toFixed(4));
  fieldProgress.textContent = String(Math.round(progress * 100)).padStart(2, "0");
};

const requestRender = () => {
  if (!frame) frame = requestAnimationFrame(render);
};

filmControl.addEventListener("click", () => {
  const paused = player.togglePlayback();
  filmControl.setAttribute("aria-label", paused ? "Play active film" : "Pause active film");
  filmControl.querySelector(".film-control__icon")!.textContent = paused ? "▶" : "Ⅱ";
  filmControl.querySelector(".film-control__label")!.textContent = paused ? "Play film" : "Pause film";
});

const onMotionChange = (event: MediaQueryListEvent) => player.setReducedMotion(event.matches);
motionQuery.addEventListener("change", onMotionChange);
addEventListener("scroll", requestRender, { passive: true });
addEventListener("resize", requestRender, { passive: true });
requestRender();
window.__ready = true;

addEventListener("pagehide", () => {
  running = false;
  if (frame) cancelAnimationFrame(frame);
  motionQuery.removeEventListener("change", onMotionChange);
  player.dispose();
}, { once: true });
